// ═══════════════════════════════════════════════════════════
// AppShell — EMC Design: single consolidated top bar
// PSPSuiteBar contains: wordmark · nav links · incident selector · right actions
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconSettings,
  IconChevronDown, IconMenu2, IconX,
  IconAntenna, IconUserPlus, IconFolderOpen, IconChalkboard,
  IconBuildingCommunity, IconForms, IconArrowsExchange, IconClipboardCheck,
  IconMap2, IconCalendarEvent, IconHomePlus, IconHomeExclamation,
  IconFlame, IconBackhoe, IconFileInvoice, IconClock, IconGitMerge,
  IconShieldCog, IconUsers, IconHistory, IconHome, IconBuilding,
  IconBuildingSkyscraper, IconChartBar, IconReportAnalytics,
} from '@tabler/icons-react';
import { GlobalSearch } from '../ui/GlobalSearch';
import { SessionTimeout } from '../ui/SessionTimeout';
import { OfflineIndicator } from '../ui/OfflineIndicator';
import { IncidentSelectorPill } from '../ui/IncidentSelectorPill';
import { useAuthStore } from '../../auth/auth-store';
import { usePermissionsStore } from '../../store/permissionsStore';
import { useShelterStore } from '../../store/shelterStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useImpactStore } from '../../store/impactStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useIntakeStore } from '../../store/intakeStore';
import { useBattleRhythmStore } from '../../store/battlerhythmStore';
import { MODULES, ENABLED_MODULES, SHOW_REPORTS_NAV, SHOW_ADMIN_NAV } from '../../modules';
import { PSPSuiteBar, PSPMenu, PSPAssistPanel } from './PSPSuiteBar';
import './AppShell.css';

// ── Icon map ──────────────────────────────────────────────────
type IconComponent = React.ComponentType<{ size?: number; color?: string }>;
const ICON_MAP: Record<string, IconComponent> = {
  IconAntenna, IconUserPlus, IconFolderOpen, IconChalkboard,
  IconBuildingCommunity, IconForms, IconArrowsExchange, IconClipboardCheck,
  IconMap2, IconCalendarEvent, IconHomePlus, IconHomeExclamation,
  IconFlame, IconBackhoe, IconFileInvoice, IconClock, IconGitMerge,
  IconShieldCog, IconUsers, IconHistory,
  IconChartBar, IconReportAnalytics,
};
function ModuleIcon({ name, size = 17 }: { name: string; size?: number }) {
  const Icon = ICON_MAP[name];
  return Icon ? <Icon size={size} /> : <IconLayoutDashboard size={size} />;
}

// ── Administration nav items ──────────────────────────────────
const ADMIN_DIRECTORY = [
  { id: 'people',        name: 'People',         description: 'Employees, volunteers & registrants', route: '/admin/people',        Icon: IconUsers              },
  { id: 'households',    name: 'Households',     description: 'Family groupings & vehicle data',     route: '/admin/households',    Icon: IconHome               },
  { id: 'facilities',    name: 'Facilities',     description: 'Shelters, staging areas & sites',     route: '/admin/facilities',    Icon: IconBuilding           },
  { id: 'organizations', name: 'Organizations',  description: 'Contractors, applicants & partners',  route: '/admin/organizations', Icon: IconBuildingSkyscraper },
  { id: 'tl-codes',      name: 'T&L Code Config', description: 'Account, shift & premium codes',      route: '/admin/tl-codes',      Icon: IconSettings           },
];
const ADMIN_SYSTEM = [
  { id: 'roles',     name: 'Roles & Permissions', description: 'Role-based access control',  route: '/admin/system/roles',     Icon: IconShieldCog      },
  { id: 'users',     name: 'Users',               description: 'User accounts & access',     route: '/admin/system/users',     Icon: IconUsers          },
  { id: 'activity',  name: 'Activity Log',         description: 'System-wide audit trail',    route: '/admin/system/activity',  Icon: IconHistory        },
  { id: 'dashboard', name: 'Dashboard Config',     description: 'Configurable widget grid',   route: '/admin/system/dashboard', Icon: IconLayoutDashboard},
  { id: 'workflows', name: 'Workflows',            description: 'Approvals, routing & tasks', route: '/workflows',              Icon: IconGitMerge       },
];

// ── Single unified nav ────────────────────────────────────────
const MAIN_NAV = [
  { label: 'Home', route: '/', exact: true, dropdown: null },
  {
    label: 'Prepare', route: null, exact: false, color: '#0d9488',
    dropdown: ['pace', 'blackboard', 'filelibrary', 'forms'],
  },
  {
    label: 'Respond', route: null, exact: false, color: '#D33423',
    dropdown: ['handoff', 'rollcall', 'intake', 'shelter', 'geoops', 'battlerhythm', 'volunteer'],
  },
  {
    label: 'Recover', route: null, exact: false, color: '#037730',
    dropdown: ['impact', 'debris', 'pagrants', 'mainstreet', 'hotwash', 'timelabor'],
  },
  {
    label: 'Insights', route: null, exact: false, color: '#4B3FFF',
    dropdown: ['reports', 'analytics'],
  },
];

// ── Group dropdown panel ──────────────────────────────────────
interface GroupDropdownProps {
  label: string;
  color?: string;
  modules: { id: string; name: string; description: string; route: string; icon: string }[];
  activeRoute: string;
  onPick: (route: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}
function GroupDropdown({ label, color = 'var(--color-primary-700)', modules, activeRoute, onPick, onMouseEnter, onMouseLeave }: GroupDropdownProps) {
  return (
    <div
      className="group-dropdown"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="group-dropdown-label" style={{ color }}>
        {label}
      </div>
      <div className="group-dropdown-grid">
        {modules.map(m => {
          const active = activeRoute.startsWith(m.route);
          return (
            <button
              key={m.id}
              className={`group-dropdown-item${active ? ' active' : ''}`}
              onClick={() => onPick(m.route)}
            >
              <span className="group-dropdown-icon" style={active ? { background: 'var(--color-primary-100)', color: 'var(--color-primary-700)' } : undefined}>
                <ModuleIcon name={m.icon} size={17} />
              </span>
              <span className="group-dropdown-text">
                <span className="group-dropdown-name">{m.name}</span>
                <span className="group-dropdown-desc">{m.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const getModuleAccess = usePermissionsStore(s => s.getModuleAccess);

  // View As
  const allRoles       = usePermissionsStore(s => s.allRoles);
  const loadAllRoles   = usePermissionsStore(s => s.loadAllRoles);
  const viewAsRoleId   = usePermissionsStore(s => s.viewAsRoleId);
  const viewAsRoleName = usePermissionsStore(s => s.viewAsRoleName);
  const setViewAs      = usePermissionsStore(s => s.setViewAs);
  const clearViewAs    = usePermissionsStore(s => s.clearViewAs);

  const [openGroup,     setOpenGroup]     = useState<string | null>(null);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [suiteMenuOpen, setSuiteMenuOpen] = useState(false);
  const [assistOpen,    setAssistOpen]    = useState(false);
  const [viewAsOpen,    setViewAsOpen]    = useState(false);
  const [viewAsSearch,  setViewAsSearch]  = useState('');
  const [loadingViewAs, setLoadingViewAs] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewAsRef  = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (viewAsRef.current && !viewAsRef.current.contains(e.target as Node)) setViewAsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Reload module stores when the active incident changes
  const reloadStores = useCallback((incidentId: string) => {
    useShelterStore.getState().loadShelters();
    useDebrisOpsStore.getState().loadLoadTickets();
    useVolunteerStore.getState().loadVolunteers();
    useImpactStore.getState().loadAssessments();
    usePAGrantsStore.getState().fetchProjects();
    useIntakeStore.getState().loadIndividuals(incidentId);
    useBattleRhythmStore.getState().loadEvents(incidentId);
    useBattleRhythmStore.getState().loadActivations(incidentId);
  }, []);

  const prevIncidentIdRef = useRef<string | null>(null);
  useEffect(() => {
    const incId = activeIncident?.id ? String(activeIncident.id) : null;
    if (incId === prevIncidentIdRef.current) return;
    prevIncidentIdRef.current = incId;
    if (incId) reloadStores(incId);
  }, [activeIncident?.id, reloadStores]);

  // Close dropdowns on navigation
  useEffect(() => {
    setViewAsOpen(false);
    setOpenGroup(null);
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Load roles when View As dropdown first opens
  useEffect(() => {
    if (viewAsOpen && allRoles.length === 0) loadAllRoles();
  }, [viewAsOpen]);

  // ⌘K / Ctrl+K → open search
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  function openMenu(label: string | null) {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenGroup(label);
  }
  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenGroup(null), 140);
  }

  function isActive(route: string, exact = false) {
    return exact ? location.pathname === route : location.pathname.startsWith(route);
  }

  // Resolve MAIN_NAV module IDs to full module objects (permission-filtered)
  const activeNav = MAIN_NAV.map(item => {
    if (!item.dropdown) return { ...item, resolvedModules: null };
    const resolved = item.dropdown
      .map(id => MODULES.find(m => m.id === id))
      .filter((m): m is NonNullable<typeof m> => !!m && getModuleAccess(m.id).canView && ENABLED_MODULES.has(m.id));
    return { ...item, resolvedModules: resolved.length > 0 ? resolved : null };
  }).filter(item => item.route !== null || item.resolvedModules !== null);

  async function handleViewAs(roleId: string, roleName: string) {
    setViewAsOpen(false);
    setViewAsSearch('');
    setLoadingViewAs(true);
    await setViewAs(roleId, roleName).catch(() => {});
    setLoadingViewAs(false);
  }

  function groupHasActive(ids: string[]) {
    return MODULES
      .filter(m => ids.includes(m.id))
      .some(m => location.pathname.startsWith(m.route));
  }

  const userInitials = (
    (user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '')
  ).toUpperCase() || (user?.email?.[0] ?? '?').toUpperCase();

  // ── Nav slot (moves into PSPSuiteBar) ─────────────────────────
  const navSlot = (
    <>
    <button
      className="mobile-nav-btn"
      onClick={() => setMobileNavOpen(o => !o)}
      aria-label="Open navigation"
    >
      <IconMenu2 size={20} />
    </button>
    <nav className="app-topnav" onMouseLeave={scheduleClose}>

      {/* Home + lifecycle groups (Prepare / Respond / Recover) */}
      {activeNav.map(item => {
        if (!item.dropdown && item.route) {
          const active = isActive(item.route, item.exact ?? false);
          return (
            <button
              key={item.label}
              className={`topnav-link${active ? ' active' : ''}`}
              onClick={() => { openMenu(null); navigate(item.route!); }}
              onMouseEnter={() => openMenu(null)}
            >
              {item.label}
            </button>
          );
        }
        const gOpen = openGroup === item.label;
        const gActive = item.dropdown ? groupHasActive(item.dropdown) : false;
        if (!item.resolvedModules) return null;
        return (
          <div key={item.label} style={{ position: 'relative' }} onMouseEnter={() => openMenu(item.label)} onMouseLeave={scheduleClose}>
            <button
              className={`topnav-link${gActive || gOpen ? ' active' : ''}`}
              style={item.color && (gActive || gOpen) ? { color: item.color, borderBottomColor: item.color } : undefined}
              onClick={() => setOpenGroup(gOpen ? null : item.label)}
            >
              {item.label}
              <IconChevronDown size={14} style={{ marginLeft: 2, marginRight: -4 }} />
            </button>
            {gOpen && (
              <GroupDropdown
                label={item.label}
                color={item.color}
                modules={item.resolvedModules}
                activeRoute={location.pathname}
                onPick={(route) => { setOpenGroup(null); navigate(route); }}
                onMouseEnter={() => openMenu(item.label)}
                onMouseLeave={scheduleClose}
              />
            )}
          </div>
        );
      })}

      {/* Reports & Analytics */}
      {SHOW_REPORTS_NAV && (() => {
        const rOpen = openGroup === '__reports__';
        const rActive = location.pathname.startsWith('/admin/analytics') || location.pathname.startsWith('/admin/reports');
        return (
          <div style={{ position: 'relative' }} onMouseEnter={() => openMenu('__reports__')} onMouseLeave={scheduleClose}>
            <button
              className={`topnav-link${rActive || rOpen ? ' active' : ''}`}
              onClick={() => setOpenGroup(rOpen ? null : '__reports__')}
            >
              Reports & Analytics
              <IconChevronDown size={14} style={{ marginLeft: 2, marginRight: -4 }} />
            </button>
            {rOpen && (
              <div className="group-dropdown" onMouseEnter={() => openMenu('__reports__')} onMouseLeave={scheduleClose} style={{ minWidth: 280 }}>
                <div style={{ padding: '8px 0' }}>
                  {[
                    { label: 'Analytics', description: 'Charts and operational trends', route: '/admin/analytics', Icon: IconHistory },
                    { label: 'Reports',   description: 'Exportable summary reports',    route: '/admin/reports',   Icon: IconFolderOpen },
                  ].map(r => {
                    const active = location.pathname.startsWith(r.route);
                    return (
                      <button key={r.label} className={`group-dropdown-item${active ? ' active' : ''}`} onClick={() => { setOpenGroup(null); navigate(r.route); }}>
                        <span className="group-dropdown-icon" style={active ? { background: 'var(--color-primary-100)', color: 'var(--color-primary-700)' } : undefined}>
                          <r.Icon size={16} />
                        </span>
                        <span className="group-dropdown-text">
                          <span className="group-dropdown-name">{r.label}</span>
                          <span className="group-dropdown-desc">{r.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Administration */}
      {SHOW_ADMIN_NAV && (() => {
        const aOpen = openGroup === '__admin__';
        const aActive = location.pathname.startsWith('/admin') || location.pathname.startsWith('/settings');
        return (
          <div style={{ position: 'relative' }} onMouseEnter={() => openMenu('__admin__')} onMouseLeave={scheduleClose}>
            <button
              className={`topnav-link${aActive || aOpen ? ' active' : ''}`}
              onClick={() => setOpenGroup(aOpen ? null : '__admin__')}
            >
              Administration
              <IconChevronDown size={14} style={{ marginLeft: 2, marginRight: -4 }} />
            </button>
            {aOpen && (
              <div className="group-dropdown" onMouseEnter={() => openMenu('__admin__')} onMouseLeave={scheduleClose} style={{ minWidth: 480 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ padding: '16px 0 8px' }}>
                    <div style={{ padding: '0 14px 8px', fontSize: 12, fontWeight: 700, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Directory</div>
                    {ADMIN_DIRECTORY.map(item => {
                      const active = location.pathname.startsWith(item.route);
                      return (
                        <button key={item.id} className={`group-dropdown-item${active ? ' active' : ''}`} onClick={() => { setOpenGroup(null); navigate(item.route); }}>
                          <span className="group-dropdown-icon" style={active ? { background: 'var(--color-primary-100)', color: 'var(--color-primary-700)' } : undefined}><item.Icon size={16} /></span>
                          <span className="group-dropdown-text">
                            <span className="group-dropdown-name">{item.name}</span>
                            <span className="group-dropdown-desc">{item.description}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ padding: '16px 0 8px', borderLeft: '1px solid var(--color-neutral-100)' }}>
                    <div style={{ padding: '0 14px 8px', fontSize: 12, fontWeight: 700, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>System</div>
                    {ADMIN_SYSTEM.map(item => {
                      const active = location.pathname.startsWith(item.route);
                      return (
                        <button key={item.id} className={`group-dropdown-item${active ? ' active' : ''}`} onClick={() => { setOpenGroup(null); navigate(item.route); }}>
                          <span className="group-dropdown-icon" style={active ? { background: 'var(--color-primary-100)', color: 'var(--color-primary-700)' } : undefined}><item.Icon size={16} /></span>
                          <span className="group-dropdown-text">
                            <span className="group-dropdown-name">{item.name}</span>
                            <span className="group-dropdown-desc">{item.description}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

    </nav>
    </>
  );

  // ── Trailing slot: Incident selector + View As ────────────────
  const trailingSlot = (
    <>
      {/* Incident selector */}
      <IncidentSelectorPill />

      {/* Divider */}
      <div style={{ width: 1, height: 20, background: 'var(--foundations-colors-gray-200)', flexShrink: 0 }} />

      {/* View As — demo tool */}
      {user && (
        <div ref={viewAsRef} style={{ position: 'relative', flexShrink: 0 }}>
          {viewAsRoleId ? (
            <button
              onClick={() => { clearViewAs(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 28, padding: '0 8px', borderRadius: 20,
                background: '#FEF3C7', border: '1px solid #F59E0B',
                color: '#92400E', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              👁 {viewAsRoleName} ×
            </button>
          ) : (
            <button
              onClick={() => setViewAsOpen(o => !o)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                height: 28, padding: '0 8px', borderRadius: 4,
                background: 'transparent', border: '1px solid var(--color-neutral-300)',
                color: 'var(--color-neutral-600)', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {loadingViewAs ? '…' : '👁 View As'}
              {!loadingViewAs && <IconChevronDown size={10} />}
            </button>
          )}

          {viewAsOpen && (
            <div style={{
              position: 'absolute', top: 34, right: 0, zIndex: 2000,
              background: '#fff', borderRadius: 4,
              border: '1px solid var(--color-neutral-200)',
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
              width: 260, overflow: 'hidden',
            }}>
              <div style={{ padding: '8px 8px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                <input
                  autoFocus
                  placeholder="Search roles…"
                  value={viewAsSearch}
                  onChange={e => setViewAsSearch(e.target.value)}
                  style={{
                    width: '100%', border: '1px solid var(--color-neutral-200)',
                    borderRadius: 4, padding: '5px 8px', fontSize: 12,
                    outline: 'none', fontFamily: 'inherit',
                  }}
                />
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {allRoles.length === 0 ? (
                  <div style={{ padding: '12px', fontSize: 12, color: 'var(--color-neutral-500)', textAlign: 'center' }}>Loading roles…</div>
                ) : (
                  allRoles
                    .filter(r => !r.isSystem && r.name.toLowerCase().includes(viewAsSearch.toLowerCase()))
                    .map(r => (
                      <button
                        key={r.id}
                        onClick={() => handleViewAs(r.id, r.name)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 16px', border: 'none',
                          background: r.id === viewAsRoleId ? 'var(--color-primary-50)' : 'transparent',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-neutral-50)')}
                        onMouseLeave={e => (e.currentTarget.style.background = r.id === viewAsRoleId ? 'var(--color-primary-50)' : 'transparent')}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-900)' }}>{r.name}</div>
                        {r.description && <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 1 }}>{r.description}</div>}
                      </button>
                    ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

    </>
  );

  return (
    <div className="app-shell">

      {/* ── Single consolidated top bar ── */}
      <PSPSuiteBar
        entity="Emergency Management Console"
        userInitials={userInitials}
        userName={user?.fullName ?? user?.email ?? ''}
        userEmail={user?.email ?? ''}
        assistOpen={assistOpen}
        navSlot={navSlot}
        trailingSlot={trailingSlot}
        onMenu={() => setSuiteMenuOpen(true)}
        onAssist={() => setAssistOpen(o => !o)}
        onAdmin={() => navigate('/admin/system/roles')}
        onSignOut={() => { logout(); navigate('/login'); }}
      />

      {/* PSP left-rail menu overlay */}
      <PSPMenu
        open={suiteMenuOpen}
        entity="Emergency Management Console"
        onClose={() => setSuiteMenuOpen(false)}
      />

      {/* ── View As banner ── */}
      {viewAsRoleId && (
        <div style={{
          background: '#FEF3C7', borderBottom: '1px solid #F59E0B',
          padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 12, color: '#92400E', flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700 }}>👁 Demo Mode</span>
          <span>Viewing as: <strong>{viewAsRoleName}</strong> — nav, modules, and data access reflect this role's permissions.</span>
          <div style={{ flex: 1 }} />
          <button
            onClick={clearViewAs}
            style={{
              background: '#F59E0B', color: '#fff', border: 'none',
              borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Exit View As
          </button>
        </div>
      )}

      {/* ── Content + optional Assist panel ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div className="app-content" style={{ flex: 1 }}>
          <SessionTimeout />
          <OfflineIndicator />
          <main className="app-main">
            <Outlet />
          </main>
        </div>

        {/* OG Assist push panel */}
        <PSPAssistPanel
          open={assistOpen}
          onClose={() => setAssistOpen(false)}
        />
      </div>

      {/* Global search overlay (⌘K) */}
      <GlobalSearch opened={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <>
          <div className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} />
          <div className="mobile-nav-drawer">
            <div className="mobile-nav-header">
              <button onClick={() => setMobileNavOpen(false)} aria-label="Close navigation">
                <IconX size={20} />
              </button>
              <span>Navigation</span>
            </div>
            <div className="mobile-nav-body">
              {activeNav.map(item => {
                if (!item.dropdown && item.route) {
                  const active = isActive(item.route, item.exact ?? false);
                  return (
                    <div key={item.label} className="mobile-nav-group">
                      <button
                        className={`mobile-nav-item${active ? ' active' : ''}`}
                        onClick={() => { navigate(item.route!); setMobileNavOpen(false); }}
                      >
                        <IconHome size={16} />
                        {item.label}
                      </button>
                    </div>
                  );
                }
                if (!item.resolvedModules) return null;
                return (
                  <div key={item.label} className="mobile-nav-group">
                    <div className="mobile-nav-group-label" style={item.color ? { color: item.color } : undefined}>
                      {item.label}
                    </div>
                    {item.resolvedModules.map(m => (
                      <button
                        key={m.id}
                        className={`mobile-nav-item${location.pathname.startsWith(m.route) ? ' active' : ''}`}
                        onClick={() => { navigate(m.route); setMobileNavOpen(false); }}
                      >
                        <ModuleIcon name={m.icon} size={16} />
                        {m.name}
                      </button>
                    ))}
                  </div>
                );
              })}
              {SHOW_REPORTS_NAV && (
              <div className="mobile-nav-group">
                <div className="mobile-nav-group-label">Reports & Analytics</div>
                {[
                  { label: 'Analytics', route: '/admin/analytics' },
                  { label: 'Reports',   route: '/admin/reports'   },
                ].map(r => (
                  <button
                    key={r.label}
                    className={`mobile-nav-item${location.pathname.startsWith(r.route) ? ' active' : ''}`}
                    onClick={() => { navigate(r.route); setMobileNavOpen(false); }}
                  >
                    <IconChartBar size={16} />
                    {r.label}
                  </button>
                ))}
              </div>
              )}
              {SHOW_ADMIN_NAV && (
              <div className="mobile-nav-group">
                <div className="mobile-nav-group-label">Administration</div>
                {[...ADMIN_DIRECTORY, ...ADMIN_SYSTEM].map(item => (
                  <button
                    key={item.id}
                    className={`mobile-nav-item${location.pathname.startsWith(item.route) ? ' active' : ''}`}
                    onClick={() => { navigate(item.route); setMobileNavOpen(false); }}
                  >
                    <item.Icon size={16} />
                    {item.name}
                  </button>
                ))}
              </div>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default AppShell;
